import React, { useMemo } from 'react';
import { isOverdue } from './MilestoneTracker';

const PRIORITY_COLORS = {
  high: { bar: 'bg-red-400', text: 'text-red-400' },
  medium: { bar: 'bg-amber-400', text: 'text-amber-400' },
  low: { bar: 'bg-zinc-500', text: 'text-zinc-500' },
};

const STATUS_COLORS = {
  todo: 'bg-zinc-700',
  in_progress: 'bg-brand-pink',
  review: 'bg-amber-400',
  done: 'bg-green-500',
};

const MilestoneTimeline = ({ milestones, onEdit }) => {
  const sorted = useMemo(() =>
    [...milestones]
      .filter(m => m.target_date)
      .sort((a, b) => new Date(a.target_date) - new Date(b.target_date)),
    [milestones]
  );

  const noDate = milestones.filter(m => !m.target_date);

  if (sorted.length === 0 && noDate.length === 0)
    return <p className="text-center text-[10px] font-black uppercase tracking-widest text-zinc-700 py-8">No milestones to show</p>;

  return (
    <div className="space-y-4">
      {sorted.length > 0 && (
        <div className="relative pl-6">
          {/* Vertical line */}
          <div className="absolute left-2.5 top-0 bottom-0 w-px bg-gradient-to-b from-brand-pink/40 via-brand-coral/20 to-transparent" />

          <div className="space-y-4">
            {sorted.map((m, i) => {
              const done = m.completed || m.status === 'done';
              const overdue = isOverdue(m);
              const pCfg = PRIORITY_COLORS[m.priority] || PRIORITY_COLORS.medium;
              const date = new Date(m.target_date);
              const isToday = date.toDateString() === new Date().toDateString();

              return (
                <div key={m.id} className="relative">
                  {/* Timeline dot */}
                  <div className={`absolute -left-[22px] top-3.5 w-3.5 h-3.5 rounded-full border-2 border-zinc-950 ${
                    done ? 'bg-green-500' : overdue ? 'bg-red-500' : isToday ? 'bg-brand-pink animate-pulse' : STATUS_COLORS[m.status || 'todo']
                  }`} />

                  <div
                    className={`p-4 rounded-2xl border transition-all ${
                      done ? 'border-white/5 bg-white/[0.02]'
                      : overdue ? 'border-red-500/20 bg-red-500/5'
                      : isToday ? 'border-brand-pink/30 bg-brand-pink/5'
                      : 'border-white/8 bg-white/[0.04] hover:bg-white/[0.06]'
                    } ${onEdit ? 'cursor-pointer' : ''}`}
                    onClick={() => onEdit && onEdit(m)}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-black leading-tight ${done ? 'line-through text-zinc-600' : overdue ? 'text-red-300' : 'text-white'}`}>
                          {m.title}
                        </p>
                        {m.description && (
                          <p className="text-xs font-bold text-zinc-500 mt-1 leading-relaxed line-clamp-1">{m.description}</p>
                        )}
                        <div className="flex flex-wrap items-center gap-2 mt-2">
                          {m.category && m.category !== 'other' && (
                            <span className="text-[9px] font-black uppercase tracking-widest text-brand-coral/70">{m.category}</span>
                          )}
                          {m.assignee_label && (
                            <span className="text-[9px] font-black text-zinc-600">👤 {m.assignee_label}</span>
                          )}
                          <span className={`text-[9px] font-black uppercase tracking-widest ${pCfg.text}`}>{m.priority}</span>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className={`text-xs font-black ${overdue && !done ? 'text-red-400' : isToday ? 'text-brand-pink' : 'text-zinc-400'}`}>
                          {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </p>
                        <p className="text-[9px] font-black text-zinc-600">{date.getFullYear()}</p>
                        {isToday && !done && <p className="text-[9px] font-black text-brand-pink uppercase tracking-widest mt-0.5">Today</p>}
                        {overdue && !done && <p className="text-[9px] font-black text-red-400 uppercase tracking-widest mt-0.5">Overdue</p>}
                        {done && <p className="text-[9px] font-black text-green-400 uppercase tracking-widest mt-0.5">Done ✓</p>}
                      </div>
                    </div>
                    {m.status === 'in_progress' && m.progress_pct > 0 && (
                      <div className="mt-3 flex items-center gap-2">
                        <div className="flex-1 h-1 rounded-full bg-white/5 overflow-hidden">
                          <div className="h-full rounded-full bg-gradient-to-r from-brand-pink to-brand-coral"
                            style={{ width: `${m.progress_pct}%` }} />
                        </div>
                        <span className="text-[9px] font-black text-brand-pink">{m.progress_pct}%</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {noDate.length > 0 && (
        <div className="space-y-2">
          <p className="text-[10px] font-black uppercase tracking-widest text-zinc-600">No Due Date</p>
          {noDate.map(m => (
            <div key={m.id} className="p-3 rounded-xl border border-white/5 bg-white/[0.02] flex items-center gap-3"
              onClick={() => onEdit && onEdit(m)}>
              <div className="w-2 h-2 rounded-full bg-zinc-700 flex-shrink-0" />
              <p className={`text-xs font-black ${m.completed ? 'line-through text-zinc-600' : 'text-zinc-400'}`}>{m.title}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MilestoneTimeline;